import React from "react";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";

type NativeDateInputType = "date" | "datetime-local" | "time";

interface InputDateProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "className" | "readOnly"
  > {
  type: NativeDateInputType;
  className: string;
}

function formatNativeDateDisplay(
  type: NativeDateInputType,
  value: string,
): string {
  if (!value) return "";

  if (type === "time") return value;

  const date = parseISO(value);
  if (!isValid(date)) return value;

  if (type === "datetime-local") {
    return format(date, "dd MMM yyyy HH:mm", { locale: es });
  }

  return format(date, "dd MMM yyyy", { locale: es });
}

/**
 * iOS WebKit renders native date inputs wider/taller than text fields.
 * A styled text display plus a transparent native overlay keeps layout consistent
 * while preserving the OS date picker.
 */
export function InputDate({
  type,
  className,
  value,
  placeholder,
  disabled,
  ...rest
}: InputDateProps): React.ReactElement {
  const stringValue = value === undefined || value === null ? "" : String(value);
  const displayValue = formatNativeDateDisplay(type, stringValue);

  return (
    <div className="input-date-shell">
      <input
        type="text"
        className={`${className} input-date-display`}
        value={displayValue}
        placeholder={placeholder ?? "Seleccionar fecha"}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
      <input
        {...rest}
        type={type}
        className={`${className} input-date-native`}
        value={stringValue}
        disabled={disabled}
      />
    </div>
  );
}
